import { createContext, useContext, useEffect, useState } from 'react';
import { useAppContext } from '../hooks/useAppContext';
import useLocalStorage from '../hooks/useLocalStorage';
import { getObjectFromStorage } from './LocalStorage';

export const DEFAULT_BACKGROUND_ID = 3;

// Both the background and tint color are stored in local storage so that we can apply it before React is initialized.
// This also allows us to easily change the design in a preview and have it update automatically.
export const COMPANY_BACKGROUND_STORAGE_KEY = 'company-design-background';
export const TINT_COLOR_STORAGE_KEY = 'small-improvements.company_color'; // Uses a different local storage API where the key is not prefixed with the user ID.

const CompanyDesignContext = createContext<{
    background: number;
    setTemporaryBackground: (background: number | undefined) => void;
} | null>(null);

export function applyBackground(background?: number) {
    const classArray = Array.from(document.body.classList);
    for (const cl of classArray) {
        if (cl.startsWith('company-background')) {
            document.body.classList.remove(cl);
        }
    }
    if (background !== undefined) {
        document.body.classList.add(`company-background${background}`);
    }
}

/**
 * Used to apply the company design stored in local storage before React is initialized.
 * This prevents the background from changing while the page is loading.
 */
export const initCompanyDesign = () => {
    const background = getObjectFromStorage(COMPANY_BACKGROUND_STORAGE_KEY, DEFAULT_BACKGROUND_ID);
    applyBackground(background);
};

export function CompanyDesignProvider({ children }: { children: React.ReactNode }) {
    const [background, setBackground] = useLocalStorage(COMPANY_BACKGROUND_STORAGE_KEY, DEFAULT_BACKGROUND_ID);
    const [temporaryBackground, setTemporaryBackground] = useState<number>();

    const { user } = useAppContext();
    const companyBackground = user.company.companyDesignSettings.companyBackground;

    useEffect(() => {
        setBackground(companyBackground ?? DEFAULT_BACKGROUND_ID);
    }, [companyBackground, setBackground]);

    useEffect(() => {
        applyBackground(temporaryBackground ?? background);
    }, [background, temporaryBackground]);

    return (
        <CompanyDesignContext.Provider value={{ background, setTemporaryBackground }}>
            {children}
        </CompanyDesignContext.Provider>
    );
}

export const useCompanyDesign = () => {
    const designContext = useContext(CompanyDesignContext);
    if (!designContext) {
        throw new Error('designContext must be used within <CompanyDesignProvider>');
    }
    return designContext;
};
